/**
 * API client for communicating with Cloudflare Worker backend
 */

const API_BASE = 'https://silentjobhunter-api.alohitsa.workers.dev/api';

// Token storage
const TOKEN_KEY = 'silentjobhunter_access_token';
const REFRESH_TOKEN_KEY = 'silentjobhunter_refresh_token';

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// API response types
export interface ApiError {
  error: string;
  message?: string;
  details?: unknown;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    publicKey: string;
    encryptedPrivateKey: string;
    emailVerified: boolean;
  };
}

export interface User {
  id: string;
  email: string;
  publicKey: string;
  encryptedPrivateKey: string;
  emailVerified: boolean;
  createdAt: string;
  lastLogin: string | null;
}

export interface Job {
  id: string;
  url: string;
  title: string | null;
  company: string | null;
  location: string | null;
  description_preview: string | null;
  salary_min: number | null;
  salary_max: number | null;
  source: string | null;
  date_posted: string | null;
  scraped_at: string;
  match_score: number | null;
  match_reason: string | null;
  notified: number;
}

export interface JobsResponse {
  jobs: Job[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface JobRun {
  id: string;
  started_at: string;
  completed_at: string | null;
  status: 'pending' | 'running' | 'completed' | 'failed';
  jobs_found: number;
  jobs_matched: number;
  error_message: string | null;
}

export interface UserStats {
  jobs: {
    total: number;
    highMatches: number;
    notified: number;
    averageScore: number | null;
  };
  runs: {
    total: number;
    successful: number;
    lastRun: string | null;
  };
  setup: {
    hasResume: boolean;
    hasPreferences: boolean;
    resumeUpdated: string | null;
    preferencesUpdated: string | null;
  };
}

// Fetch wrapper with auth
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAccessToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle token refresh on 401
  if (response.status === 401 && token) {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      try {
        const refreshResponse = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        if (refreshResponse.ok) {
          const { accessToken, refreshToken: newRefreshToken } = await refreshResponse.json();
          setTokens(accessToken, newRefreshToken);

          // Retry original request
          (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
          const retryResponse = await fetch(`${API_BASE}${endpoint}`, {
            ...options,
            headers,
          });

          if (!retryResponse.ok) {
            const error = await retryResponse.json();
            throw new Error(error.error || 'Request failed');
          }

          return retryResponse.json();
        }
      } catch {
        // Refresh failed, clear tokens
        clearTokens();
        window.location.href = '/login';
      }
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || error.message || 'Request failed');
  }

  return response.json();
}

// Auth API
export const authApi = {
  register: (data: {
    email: string;
    password: string;
    encryptedPrivateKey: string;
    publicKey: string;
  }): Promise<AuthResponse> =>
    apiFetch('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  login: (email: string, password: string): Promise<AuthResponse> =>
    apiFetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: (refreshToken?: string): Promise<{ success: boolean }> =>
    apiFetch('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }),

  refresh: (refreshToken: string): Promise<{ accessToken: string; refreshToken: string }> =>
    apiFetch('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }),
};

// User API
export const userApi = {
  getProfile: (): Promise<User> => apiFetch('/me'),

  getStats: (): Promise<UserStats> => apiFetch('/me/stats'),

  deleteAccount: (): Promise<{ success: boolean }> =>
    apiFetch('/me', { method: 'DELETE' }),
};

// Resume API
export const resumeApi = {
  get: (): Promise<{
    encryptedResume: string;
    resumeHash: string;
    resumeSummary: string | null;
    resumeKeywords: string[] | null;
    uploadedAt: string;
  }> => apiFetch('/resume'),

  getMetadata: (): Promise<{
    resumeHash: string;
    resumeSummary: string | null;
    resumeKeywords: string[] | null;
    uploadedAt: string;
    hasFile: boolean;
  }> => apiFetch('/resume/metadata'),

  upload: (
    encryptedResume: string,
    resumeHash: string,
    resumeText?: string  // Plaintext for AI summarization
  ): Promise<{ success: boolean; hashChanged: boolean; message?: string }> =>
    apiFetch('/resume', {
      method: 'POST',
      body: JSON.stringify({ encryptedResume, resumeHash, resumeText }),
    }),

  delete: (): Promise<{ success: boolean }> =>
    apiFetch('/resume', { method: 'DELETE' }),
};

// Preferences API
export const preferencesApi = {
  get: (): Promise<{
    encryptedPreferences: string;
    preferencesHash: string;
    preferencesSummary: string | null;
    updatedAt: string;
  }> => apiFetch('/preferences'),

  update: (
    encryptedPreferences: string,
    preferencesHash: string,
    preferencesText?: string  // Plaintext for AI summarization
  ): Promise<{ success: boolean; hashChanged: boolean; message?: string }> =>
    apiFetch('/preferences', {
      method: 'PUT',
      body: JSON.stringify({ encryptedPreferences, preferencesHash, preferencesText }),
    }),

  delete: (): Promise<{ success: boolean }> =>
    apiFetch('/preferences', { method: 'DELETE' }),
};

// Jobs API
export const jobsApi = {
  list: (params?: {
    page?: number;
    pageSize?: number;
    minScore?: number;
    source?: string;
    notified?: boolean;
  }): Promise<JobsResponse> => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
    if (params?.minScore) query.set('minScore', params.minScore.toString());
    if (params?.source) query.set('source', params.source);
    if (params?.notified !== undefined) query.set('notified', params.notified.toString());

    const queryStr = query.toString();
    return apiFetch(`/jobs${queryStr ? `?${queryStr}` : ''}`);
  },

  get: (id: string): Promise<Job> => apiFetch(`/jobs/${id}`),

  getStats: (): Promise<{
    bySource: Array<{ source: string; count: number; avg_score: number; high_matches: number }>;
    scoreDistribution: Array<{ range: string; count: number }>;
  }> => apiFetch('/jobs/stats'),

  getRuns: (limit?: number): Promise<{ runs: JobRun[] }> =>
    apiFetch(`/jobs/runs${limit ? `?limit=${limit}` : ''}`),

  triggerRun: (): Promise<{ success: boolean; runId: string }> =>
    apiFetch('/jobs/run', { method: 'POST' }),

  cancelRun: (runId: string): Promise<{ success: boolean; message: string }> =>
    apiFetch(`/jobs/run/${runId}/cancel`, { method: 'POST' }),

  clearStaleRuns: (): Promise<{ success: boolean; cleared: number }> =>
    apiFetch('/jobs/runs/clear-stale', { method: 'POST' }),

  markNotified: (jobIds: string[]): Promise<{ success: boolean }> =>
    apiFetch('/jobs/notify', {
      method: 'POST',
      body: JSON.stringify({ jobIds }),
    }),
};
